/**
 * FolderRenderer Component
 *
 * Recursive rendering of folder children with drag-and-drop support.
 */

import {
  DndContext,
  closestCorners,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import { SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy } from '@dnd-kit/sortable';
import type { Space } from '@/types';
import type { SpaceItems } from '@/store/appStore';
import type { FolderData, ListData } from './types';
import type { SpaceItem } from '@/db/types';
import type { ContextMenuType } from '../ProjectContextMenu';
import { SortableFolderItem } from '../SortableFolderItem';
import { SortableListItem } from '../SortableListItem';
import { SortableGenericItem } from '../SortableGenericItem';

interface FolderRendererProps {
  containerId: string;
  space: Space;
  containerItems: Record<string, string[]>;
  folderDataMap: Record<string, FolderData>;
  listDataMap: Record<string, ListData>;
  expandedFolders: Set<string>;
  spaceItems?: Record<string, SpaceItems>;
  currentItemType: string | null;
  currentDocument: { id: string } | null;
  currentChat: { id: string } | null;
  currentVoiceChannel: { id: string } | null;
  currentSpreadsheet: { id: string } | null;
  currentPresentation: { id: string } | null;
  isActive: (path: string | null) => boolean;
  toggleFolder: (key: string) => void;
  handleFolderClick: (spaceId: string, folderId: string) => void;
  handleListClick: (spaceId: string, folderId: string | null, listId: string) => void;
  handleViewModeChange: (mode: 'list') => void;
  onOpenContextMenu: (e: React.MouseEvent, type: ContextMenuType, spaceName: string, folderName?: string, listName?: string, itemId?: string, itemType?: string, itemName?: string) => void;
  onItemClick?: (type: 'document' | 'chat' | 'voice_channel' | 'spreadsheet' | 'presentation', id: string) => void;
  onDragEnd: (event: DragEndEvent) => void;
  depth?: number;
}

export function FolderRenderer({
  containerId,
  space,
  containerItems,
  folderDataMap,
  listDataMap,
  expandedFolders,
  spaceItems,
  currentItemType,
  currentDocument,
  currentChat,
  currentVoiceChannel,
  currentSpreadsheet,
  currentPresentation,
  isActive,
  toggleFolder,
  handleFolderClick,
  handleListClick,
  handleViewModeChange,
  onOpenContextMenu,
  onItemClick,
  onDragEnd,
  depth = 0,
}: FolderRendererProps) {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  if (!containerItems[containerId]) return null;

  return (
    <>
      {containerItems[containerId].map((childPrefixedId) => {
        const [childPrefix, ...childIdParts] = childPrefixedId.split('-');
        const childItemId = childIdParts.join('-');

        if (childPrefix === 'folder') {
          const folderData = folderDataMap[childItemId];
          if (!folderData) return null;

          const nestedFolderKey = `${space.id}/nested-${childItemId}`;
          const nestedFolderIsExpanded = expandedFolders.has(nestedFolderKey);

          const nestedFolder = {
            id: childItemId,
            name: folderData.name,
            lists: [],
            taskCount: 0,
            type: 'folder' as const,
          };

          return (
            <div key={childPrefixedId}>
              <SortableFolderItem
                folder={nestedFolder}
                folderId={childItemId}
                spaceName={space.name}
                folderKey={nestedFolderKey}
                isActive={isActive(`${space.id}/${childItemId}`)}
                isExpanded={nestedFolderIsExpanded}
                onToggle={() => toggleFolder(nestedFolderKey)}
                onClick={() => {
                  handleFolderClick(space.id, childItemId);
                  handleViewModeChange('list');
                }}
                onContextMenu={(e) => onOpenContextMenu(e, 'folder', space.name, nestedFolder.name)}
              >
                {nestedFolderIsExpanded && containerItems[childItemId] && (
                  <DndContext sensors={sensors} collisionDetection={closestCorners} onDragEnd={onDragEnd}>
                    <SortableContext items={containerItems[childItemId]} strategy={verticalListSortingStrategy}>
                      <div className="ml-4 pl-2 border-l border-border-primary/30 mt-0.5 space-y-0.5">
                        <FolderRenderer
                          containerId={childItemId}
                          space={space}
                          containerItems={containerItems}
                          folderDataMap={folderDataMap}
                          listDataMap={listDataMap}
                          expandedFolders={expandedFolders}
                          spaceItems={spaceItems}
                          currentItemType={currentItemType}
                          currentDocument={currentDocument}
                          currentChat={currentChat}
                          currentVoiceChannel={currentVoiceChannel}
                          currentSpreadsheet={currentSpreadsheet}
                          currentPresentation={currentPresentation}
                          isActive={isActive}
                          toggleFolder={toggleFolder}
                          handleFolderClick={handleFolderClick}
                          handleListClick={handleListClick}
                          handleViewModeChange={handleViewModeChange}
                          onOpenContextMenu={onOpenContextMenu}
                          onItemClick={onItemClick}
                          onDragEnd={onDragEnd}
                          depth={depth + 1}
                        />
                      </div>
                    </SortableContext>
                  </DndContext>
                )}
              </SortableFolderItem>
            </div>
          );
        } else if (childPrefix === 'list') {
          const listData = listDataMap[childItemId];
          if (!listData) return null;

          const list = {
            id: childItemId,
            name: listData.name,
            taskCount: listData.taskCount,
          };

          const folderName = listData.folderId ? folderDataMap[listData.folderId]?.name || null : null;
          const folderId = listData.folderId || null;

          return (
            <SortableListItem
              key={childPrefixedId}
              list={list}
              listId={childItemId}
              spaceName={space.name}
              folderName={folderName}
              isActive={isActive(folderId ? `${space.id}/${folderId}/${childItemId}` : `${space.id}/${childItemId}`)}
              onClick={() => {
                handleListClick(space.id, folderId, childItemId);
                handleViewModeChange('list');
              }}
              onContextMenu={(e) => onOpenContextMenu(e, 'list', space.name, folderName || undefined, list.name)}
            />
          );
        } else if (childPrefix === 'item') {
          const items = (spaceItems?.[space.name] || []) as SpaceItem[];
          const item = items.find((i: SpaceItem) => i.id === childItemId);
          if (!item || !item.type) return null;

          const itemType = item.type as 'document' | 'chat' | 'voice_channel' | 'spreadsheet' | 'presentation';
          const isCurrent =
            currentItemType === itemType &&
            ((itemType === 'document' && currentDocument?.id === item.id) ||
              (itemType === 'chat' && currentChat?.id === item.id) ||
              (itemType === 'voice_channel' && currentVoiceChannel?.id === item.id) ||
              (itemType === 'spreadsheet' && currentSpreadsheet?.id === item.id) ||
              (itemType === 'presentation' && currentPresentation?.id === item.id));

          return (
            <SortableGenericItem
              key={childPrefixedId}
              item={{ ...item, type: itemType }}
              isActive={isCurrent}
              onClick={() => onItemClick?.(itemType, item.id)}
              onContextMenu={(e) => onOpenContextMenu(e, 'item', space.name, undefined, undefined, item.id, itemType, item.name)}
            />
          );
        }

        return null;
      })}
    </>
  );
}
